import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pin, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoNews = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adsShown, setAdsShown] = useState(0);

  useEffect(() => {
    fetchNews();
    fetchAds();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news?limit=50`);
      setNews(response.data.news || []);
    } catch (error) {
      console.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/feed/ads`);
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Failed to load ads');
    }
  };

  const pinnedNews = news.filter(n => n.pinned);
  const regularNews = news.filter(n => !n.pinned);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">GoNews</h1>
          <p className="text-muted-foreground">Official announcements and updates from GoFella</p>
        </div>

        {loading ? (
          <div className="text-center py-20">Loading news...</div>
        ) : (
          <div className="space-y-4">
            {/* Pinned News */}
            {pinnedNews.map(item => (
              <Card key={item.id} className="border-2 border-yellow-300 dark:border-yellow-700">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Pin className="w-5 h-5 text-yellow-600 mt-1" />
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-foreground mb-2">{item.title}</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">{item.body}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Product Ad after pinned */}
            {pinnedNews.length > 0 && ads.length > 0 && (
              <Card className="border-2 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4">
                  <Badge variant="outline" className="text-xs mb-2">Sponsored</Badge>
                  <h3 className="font-bold text-lg mb-1">{ads[0].title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{ads[0].subtitle}</p>
                  <Button size="sm" onClick={() => navigate(ads[0].cta_url)}>{ads[0].cta_text}</Button>
                </CardContent>
              </Card>
            )}
            
            {/* Regular News with ads every 8 items */}
            {regularNews.map((item, idx) => {
              const shouldShowAd = idx > 0 && idx % 8 === 0 && adsShown < 3 && ads.length > 0;
              const ad = shouldShowAd ? ads[(adsShown + 1) % ads.length] : null;
              
              return (
                <React.Fragment key={item.id}>
                  {ad && (() => { setAdsShown(prev => prev + 1); return (
                    <Card className="border-2 border-blue-200 dark:border-blue-800">
                      <CardContent className="p-4">
                        <Badge variant="outline" className="text-xs mb-2">Sponsored</Badge>
                        <h3 className="font-bold text-lg mb-1">{ad.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{ad.subtitle}</p>
                        <Button size="sm" onClick={() => navigate(ad.cta_url)}>{ad.cta_text}</Button>
                      </CardContent>
                    </Card>
                  ); })()}
                  
                  <Card>
                    <CardContent className="p-6">
                      <h2 className="text-2xl font-bold text-foreground mb-2">{item.title}</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">{item.body}</p>
                    </CardContent>
                  </Card>
                </React.Fragment>
              );
            })}
            
            {!loading && news.length === 0 && (
              <div className="text-center py-20 text-muted-foreground">No news available</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoNews;
